import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  eventListener,
  IAccountService,
  IPropertySelection,
  IUserContext,
  TOPICS,
} from "@origin-digital/event-dispatcher";
import { isAuthenticated } from "@origin-digital/platform-helpers";
import { useRespondWhenDefined } from "../hooks/useRespondWhenDefined";
import { useAutoFetchUserContext } from "../hooks/useAutoFetchUserContext";
import { usePropertySelection } from "../hooks/usePropertySelection";
import { useOnStaleUserData } from "../hooks/useOnStaleUserData";
import { useUserContextSubscriptionHandler } from "../hooks/useUserContextSubscriptionHandler";

export type UserContextType = {
  user?: IUserContext;
  loading: boolean;
  properties?: IPropertySelection[];
  property: IPropertySelection | null;
  setProperty: (
    value: IPropertySelection | null,
    persist?: boolean,
    applyServiceQueryParam?: boolean
  ) => void;
  setService: (accountService: IAccountService) => void;
};

export const UserContext = createContext<UserContextType>({
  user: undefined,
  loading: true,
  properties: undefined,
  property: null,
  setProperty: () => {},
  setService: () => {},
});

export const useUserContext = () => useContext(UserContext);

const isLoginTokenPresent = () => {
  if (typeof window !== "undefined") {
    // Happy to use the lazy synchronous check here, doesn't need to be 100% accurate
    // check window and document to avoid SSR failures
    return isAuthenticated();
  }
  return false;
};

export const UserContextProvider = ({ children }: { children: ReactNode }) => {
  const [loggedIn, setLoggedIn] = useState(isLoginTokenPresent());
  const tokenPresent = isLoginTokenPresent();
  if (tokenPresent !== loggedIn) {
    setLoggedIn(isLoginTokenPresent());
  }

  const {
    data: user,
    loading,
    refresh: refreshUserContext,
  } = useAutoFetchUserContext();
  useRespondWhenDefined(TOPICS.FETCH_USER_CONTEXT, user, 5000);
  useUserContextSubscriptionHandler(user);
  useOnStaleUserData(refreshUserContext);

  useEffect(() => {
    refreshUserContext();
  }, [refreshUserContext, loggedIn]);

  useEffect(() => {
    return eventListener.addListener(
      TOPICS.REFRESH_USER_CONTEXT,
      refreshUserContext
    );
  }, [refreshUserContext]);

  const { properties, property, setProperty, setService } =
    usePropertySelection(user);
  useRespondWhenDefined(TOPICS.FETCH_ALL_PROPERTIES, properties);

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        properties,
        property,
        setProperty,
        setService,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
